// src/features/alerts/components/AlertSeverityBadge.tsx

import React from 'react';
import { Chip } from '@mui/material';
import { useTranslate } from 'react-admin';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

type Severity = 'INFO' | 'WARNING' | 'CRITICAL';

interface AlertSeverityBadgeProps {
  severity: Severity | string;
}

/**
 * Badge showing alert severity with icon and color
 * Handles different severity formats (uppercase, lowercase, mixed)
 */
export const AlertSeverityBadge: React.FC<AlertSeverityBadgeProps> = ({ severity }) => {
  const translate = useTranslate();

  // Return null if no severity provided
  if (!severity) {
    return null;
  }

  const config = {
    INFO: {
      color: 'info' as const,
      icon: <InfoIcon />,
    },
    WARNING: {
      color: 'warning' as const,
      icon: <WarningIcon />,
    },
    CRITICAL: {
      color: 'error' as const,
      icon: <ErrorIcon />,
    },
  };

  // Normalize severity to uppercase
  const normalizedSeverity = (severity?.toString().toUpperCase() || 'INFO') as Severity;
  
  // Get config or default to INFO
  const { color, icon } = config[normalizedSeverity] || config.INFO;

  return (
    <Chip
      icon={icon}
      label={translate(`resources.alerts.severity.${normalizedSeverity.toLowerCase()}`)}
      color={color}
      size="small"
    />
  );
};